import React, { useEffect, useRef, useState } from 'react';
import type { AuthorProfile, ChatMessage, CommunityChat } from './types';
import { AuthorBadge } from './CommunityShared';
import { adminMemberProfile, formatForumDate, isOwnCommunityMessage, memberAuthorFromProfile } from './communityUtils';
import { useCommunityForum } from './CommunityForumContext';
import { CommunityDevEntity, CommunityDevPath } from './CommunityDevUi';
import {
  CommunityMessageAdminActions,
  CommunityMessageContent,
  useCommunityMessageEdit,
} from './CommunityMessageActions';
import { entityRef, P } from './communityDevMode';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  chat: CommunityChat;
  realIsAdmin: boolean;
};

type ChatMessageRowProps = {
  msg: ChatMessage;
  isOwn: boolean;
  isAdmin: boolean;
  realIsAdmin: boolean;
  onSave: (body: string) => void;
  onDelete: () => void;
};

function ChatMessageRow({ msg, isOwn, isAdmin, realIsAdmin, onSave, onDelete }: ChatMessageRowProps) {
  const edit = useCommunityMessageEdit(msg.body, onSave);

  return (
    <CommunityDevEntity storagePath={entityRef(P.chatMessages, msg.id)}>
      <article className="dcf-chat__message">
        <div className="dcf-chat__message-head">
          <AuthorBadge author={msg.author} devPath={P.chatMessageAuthor} />
          <div className="dcf-message-meta">
            <time className="dcf-chat__time" dateTime={msg.updatedAt || msg.createdAt}>
              {formatForumDate(msg.updatedAt || msg.createdAt)}
            </time>
            <CommunityMessageAdminActions
              canEdit={isAdmin && isOwn}
              canDelete={isAdmin}
              realIsAdmin={realIsAdmin}
              isAdmin={isAdmin}
              deleteLabel="Удаление сообщения в чате"
              storagePath={entityRef(P.chatMessages, msg.id)}
              onEdit={edit.startEdit}
              onDelete={onDelete}
              hidden={edit.editing}
            />
          </div>
        </div>
        <CommunityMessageContent
          className="dcf-chat__message-body"
          body={msg.body}
          bodyDevPath={<CommunityDevPath path={P.chatMessageBody} inline />}
          editing={edit.editing}
          draft={edit.draft}
          setDraft={edit.setDraft}
          cancelEdit={edit.cancelEdit}
          saveEdit={edit.saveEdit}
        />
      </article>
    </CommunityDevEntity>
  );
}

export default function CommunityChatView({ chat, realIsAdmin }: Props) {
  const { user } = useAuth();
  const { getMessages, addChatMessage, editChatMessage, removeChatMessage, loadChatMessages, isAdmin } = useCommunityForum();
  const messages = getMessages(chat.id);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadChatMessages(chat.id);
  }, [chat.id, loadChatMessages]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const latest = messages[messages.length - 1];
      const since = latest?.updatedAt || latest?.createdAt;
      void loadChatMessages(chat.id, since);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [chat.id, loadChatMessages, messages]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, chat.id]);

  const currentAuthor = (): AuthorProfile =>
    isAdmin ? adminMemberProfile() : memberAuthorFromProfile(false, user?.email);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    void addChatMessage(chat.id, text, currentAuthor(), user?.email);
    setDraft('');
  };

  return (
    <div className="dcf-chat">
      <header className="dcf-header">
        <h1 className="dcf-header__title">
          {chat.name}
          <CommunityDevPath path={P.chatName} inline />
        </h1>
        {chat.description && (
          <p className="dcf-header__desc">
            {chat.description}
            <CommunityDevPath path={P.chatDescription} inline />
          </p>
        )}
        <CommunityDevPath path={entityRef(P.chats, chat.id)} />
        <p className="dcf-header__desc text-xs text-slate-500 mt-1">
          Текстовый чат · {messages.length} сообщений
        </p>
      </header>

      <div className="dcf-chat__messages" ref={listRef}>
        {messages.length === 0 ? (
          <div className="dcf-empty dcf-empty--compact">
            <p className="dcf-empty__title">Пока нет сообщений</p>
            <p className="dcf-empty__text">Напишите первое сообщение в этом чате.</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = isOwnCommunityMessage(msg.authorKey, user?.email);
            return (
              <ChatMessageRow
                key={msg.id}
                msg={msg}
                isOwn={isOwn}
                isAdmin={isAdmin}
                realIsAdmin={realIsAdmin}
                onSave={(body) => { void editChatMessage(msg.id, body); }}
                onDelete={() => { void removeChatMessage(msg.id); }}
              />
            );
          })
        )}
      </div>

      <footer className="dcf-chat__composer">
        <CommunityDevPath path={P.chatMessageBody} />
        <textarea
          className="dcf-textarea dcf-chat__input"
          rows={2}
          placeholder="Сообщение…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button type="button" className="dcf-btn dcf-btn--primary" onClick={send}>
          Отправить
        </button>
      </footer>
    </div>
  );
}
